<template>
  <div class="product">
    <template v-if="!isLoadingProduct && !productNotFound">
      <v-container fluid>
        <v-row dense>
          <v-col cols="12">
            <v-card class="mx-auto" max-width="600px">
              <v-img
                v-if="productImage"
                :src="productImage"
                class="white--text align-end"
                gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                :height="cardDimensions.imgHeight"
              ></v-img>

              <div class="d-flex justify-center py-2 grey lighten-4" style="width: 100%" v-else>
                <v-avatar size="150px" tile>
                  <v-icon x-large>mdi-tag</v-icon>
                </v-avatar>
              </div>

              <v-form class="pa-4" ref="form" v-model="valid" lazy-validation>
                <v-text-field
                  ref="name"
                  v-model="productName"
                  :rules="nameRules"
                  label="Name"
                  color="primary darken-4"
                  :class="{ 'mb-3': !valid }"
                  outlined
                  dense
                  @focus="activeField('name')"
                  @blur="activeField(null)"
                  @input="unsavedChanges('name')"
                  :readonly="isStaff"
                >
                  <template v-if="isActive == 'name'" v-slot:append>
                    <v-icon
                      color="primary darken-4"
                      :disabled="isSaved.name"
                      @click="saveChanges('name')"
                    >mdi-content-save</v-icon>
                  </template>
                </v-text-field>

                <v-text-field
                  ref="price"
                  type="number"
                  v-model.number="productPrice"
                  :rules="priceRules"
                  prefix="GH₵"
                  label="Price"
                  color="primary darken-4"
                  :class="{ 'mb-3': !valid }"
                  outlined
                  dense
                  @focus="activeField('price')"
                  @blur="activeField(null)"
                  @input="unsavedChanges('price')"
                  :readonly="isStaff"
                >
                  <template v-if="isActive == 'price'" v-slot:append>
                    <v-icon
                      color="primary darken-4"
                      :disabled="isSaved.price"
                      @click="saveChanges('price')"
                    >mdi-content-save</v-icon>
                  </template>
                </v-text-field>

                <v-row>
                  <v-col align="center" cols="2">
                    <v-fade-transition leave-absolute>
                      <v-progress-circular
                        v-if="loadingIsQuantifiable"
                        class="mt-0"
                        size="24"
                        color="primary darken-4"
                        indeterminate
                      ></v-progress-circular>

                      <v-checkbox
                        v-model="productIsQuantifiable"
                        color="primary darken-4"
                        hide-details
                        class="mt-0"
                        @change="saveChangesQuantifiable"
                        :readonly="isStaff"
                        v-else
                      ></v-checkbox>
                    </v-fade-transition>
                  </v-col>

                  <v-col align="center" cols="10">
                    <v-text-field
                      ref="quantity"
                      type="number"
                      v-model.number="productQuantity"
                      :rules="quantityRules"
                      label="Quantity"
                      hint="Check the box to the left, if this product is quantifiable."
                      persistent-hint
                      color="primary darken-4"
                      :class="{ 'mb-3': !valid }"
                      outlined
                      dense
                      :readonly="true"
                      @blur="activeField(null)"
                    >
                      <template v-slot:append-outer>
                        <v-btn
                          class="my-auto"
                          @click="openEditProductQuantityDialog()"
                          :disabled="isStaff || !productIsQuantifiable"
                          icon
                        >
                          <v-icon class="success--text">mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-select
                  v-model="productPrinter"
                  :rules="printerRules"
                  :items="printerChoices"
                  label="Printer"
                  hint="Select a Printer."
                  persistent-hint
                  color="primary darken-4"
                  class="mb-3"
                  dense
                  :readonly="isStaff"
                  @change="saveChangesPrinter"
                ></v-select>

                <v-textarea
                  ref="description"
                  v-model="productDescription"
                  label="Description"
                  color="primary darken-4"
                  validate-on-blur
                  hide-details
                  :rows="1"
                  outlined
                  dense
                  auto-grow
                  @focus="activeField('description')"
                  @blur="activeField(null)"
                  @input="unsavedChanges('description')"
                  :readonly="isStaff"
                >
                  <template v-if="isActive == 'description'" v-slot:append>
                    <v-icon
                      color="primary darken-4"
                      :disabled="isSaved.description"
                      @click="saveChanges('description')"
                    >mdi-content-save</v-icon>
                  </template>
                </v-textarea>
              </v-form>

              <div class="d-flex justify-end">
                <product-delete v-model="removeProduct" v-if="!isStaff">
                  <template #activator>
                    <v-btn class="ma-3 pa-1 text-capitalize" text color="error">
                      <v-icon>mdi-delete</v-icon>Delete
                    </v-btn>
                  </template>
                </product-delete>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <v-container v-show="isLoadingProduct || productNotFound">
      <v-row justify="center" align="center">
        <v-fade-transition leave-absolute>
          <v-col cols="auto" v-if="isLoadingProduct">
            <v-progress-circular size="32" color="primary darken-4" indeterminate></v-progress-circular>
          </v-col>

          <v-col v-else cols="auto">
            <v-card color="white" class="pa-8" light elevation="2" :width="cardWidth">
              <div class="d-flex justify-center grey--text">
                <v-icon size="150">mdi-cancel</v-icon>
              </div>

              <div class="text-center title grey--text">
                <span class="font-weight-black">Product</span> not available
              </div>
            </v-card>
          </v-col>
        </v-fade-transition>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import ProductDelete from "@/components/products/ProductDelete";

export default {
  name: "Product",
  data() {
    return {
      isLoadingProduct: false,
      valid: true,
      isActive: null,
      isSaved: {
        name: true,
        price: true,
        quantity: true,
        description: true
      },
      image: "",
      name: "",
      nameRules: [v => !!v || "name is required"],
      price: 0.0,
      priceRules: [
        v => !!v || "Update price",
        v => v > 0 || "Price must be greater than 0"
      ],
      printerChoices: [
        { text: "Printer A", value: "A" },
        { text: "Printer B", value: "B" }
      ],
      printerRules: [v => !!v || "A printer is required"],
      loadingIsQuantifiable: false,
      quantity: 0,
      description: "",
      removeProduct: null,
      card: {
        title: "Food",
        src: "https://cdn.vuetifyjs.com/images/cards/house.jpg",
        flex: 12
      },
      updater: null
    };
  },

  components: {
    ProductDelete
  },

  methods: {
    async getProduct() {
      this.isLoadingProduct = true;
      var productId = this.$route.params.productId;

      await this.$store.dispatch("getProduct", productId).finally(() => {
        this.isLoadingProduct = false;
      });
    },

    async getProduct2() {
      var productId = this.$route.params.productId;

      await this.$store.dispatch("getProduct", productId);
    },

    openEditProductQuantityDialog() {
      this.$store.commit("setEditProductQuantityDialog", true);
    },

    activeField(reference) {
      this.isActive = reference;
    },

    unsavedChanges(reference) {
      this.$set(this.isSaved, `${reference}`, false);
    },

    async saveChanges(reference) {
      if (!this.$refs[`${reference}`].errorBucket.length > 0) {
        var productId = this.$route.params.productId;

        await this.$store
          .dispatch("updateProduct", {
            reference,
            productId,
            data: this[`${reference}`]
          })
          .then(() => {
            this.$set(this.isSaved, `${reference}`, true);
          })
          .catch(() => {});
      }
    },

    async saveChangesQuantifiable(value) {
      this.loadingIsQuantifiable = true;
      var productId = this.$route.params.productId;

      await this.$store
        .dispatch("updateProduct", {
          reference: "is_quantifiable",
          productId,
          data: value
        })
        .then(() => {
          this.loadingIsQuantifiable = false;
        })
        .catch(() => {
          this.loadingIsQuantifiable = false;
        });
    },

    async saveChangesPrinter(value) {
      var productId = this.$route.params.productId;

      await this.$store
        .dispatch("updateProduct", {
          reference: "printer",
          productId,
          data: value
        })
        .then(() => {})
        .catch(() => {});
    },

    async deleteProduct() {
      if (this.removeProduct && this.removeProduct.confirmed) {
        var productId = this.$route.params.productId;

        await this.$store.dispatch("deleteProduct", productId).finally(() => {
          this.$router.go(-1);
        });
      }
    }
  },

  beforeRouteLeave(to, from, next) {
    clearInterval(this.updater);
    next();
  },

  watch: {
    $route() {
      this.getProduct();
    },
    removeProduct: "deleteProduct"
  },

  mounted() {
    this.getProduct();

    this.updater = setInterval(() => {
      this.getProduct2();
    }, 5000);
  },

  computed: {
    cardDimensions() {
      const nameType = this.$vuetify.breakpoint.name;
      var value = 0;

      switch (nameType) {
        case "xs":
          value = { imgHeight: "150px" };
          break;
        case "sm":
        case "md":
          value = { imgHeight: "200px" };
          break;
        case "lg":
        case "xl":
          value = { imgHeight: "200px" };
          break;

        default:
          value = { imgHeight: "200px" };
      }

      return value;
    },

    quantityRules() {
      const rules = [];

      if (this.productIsQuantifiable) {
        const rule1 = v => v !== "" || "Enter a number";
        rules.push(rule1);
        const rule2 = v => v >= 0 || "Quantity must be a non-negative number";
        rules.push(rule2);
      }

      return rules;
    },

    productNotFound() {
      return this.$store.state.products.productNotFound;
    },

    productImage() {
      return this.$store.state.products.product.image;
    },

    productName: {
      get() {
        return this.$store.state.products.product.name;
      },
      set: function(value) {
        this.name = value;
      }
    },

    productPrice: {
      get() {
        return this.$store.state.products.product.price;
      },
      set: function(value) {
        this.price = value;
      }
    },

    productIsQuantifiable: {
      get: function() {
        return this.$store.state.products.product.is_quantifiable;
      },
      set: function() {
        return;
      }
    },

    productQuantity: {
      get() {
        return this.$store.state.products.product.quantity;
      },
      set: function(value) {
        this.quantity = value;
      }
    },

    productDescription: {
      get() {
        return this.$store.state.products.product.description;
      },
      set: function(value) {
        this.description = value;
      }
    },

    productPrinter: {
      get: function() {
        return this.$store.state.products.product.printer;
      },
      set: function() {
        return;
      }
    },

    cardWidth() {
      var name = this.$vuetify.breakpoint.name;

      if (name === "xs") {
        return "99vw";
      } else {
        return "45vw";
      }
    },

    isStaff() {
      if (this.$store.state.user.user_type === "Staff") return true;
      else return false;
    }
  }
};
</script>

<style>
</style>